<template>
  <main class="main">
    <div class="container">
      <LazyUiBreadCrumbsV2
        :items="[
          {
            title: t('collections') || '',
            link: localePath('/collections'),
          },
          {
            title: collectionInfo.name || '',
            link: localePath((`/collections/${route.params.id}`) as string),
          },
        ]"
      />
      <PagesCollectionsCollectionPage
        v-model:page="page"
        :info="collectionInfo"
        :items="items"
        :limit="LIMIT"
        :loading="status === 'pending'"
        :pages-length="pagesLength"
        :total-items-length="pagesLength * LIMIT"
      />
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { AdvancedSearchBody, CollectionInfo } from '~/graphql/generated/schema-types';
import { useAlternateLinks } from '~/composables/alternate-links';
import { getCollectionInfo, getCollectionItems } from '~/utils/api';
import { getImageFromWaxpeer } from '~/utils/get-image-url';

const localePath = useLocalePath();
const { t, locale } = useI18n();
const route = useRoute();

const LIMIT = 50;
const page = ref(1);
const config = useRuntimeConfig();
const siteHost = config.public.siteHost;

const { data: infoData } = await useAsyncData(`getCollectionInfo${route.params.id}`, () =>
  getCollectionInfo(route.params.id as string));

const { error, data, status } = await useAsyncData(
  `getCollectionItems${route.params.id}`,
  () =>
    getCollectionItems(
      [infoData?.value?.data?.collectionInfo?.name as string],
      page.value,
      locale.value,
    ),
  { watch: [page] },
);

if (error.value || data?.value?.errors?.length) {
  throw createError({
    status: 404,
    statusText:
        ((`Collection page error: ${route.params.id}`) as string)
        + (error.value?.message || data?.value?.errors?.[0]?.message || ''),
  });
}

const collectionInfo = computed(
  () => (infoData?.value?.data?.collectionInfo || {}) as CollectionInfo,
);

const items = computed(() => {
  return ((data?.value?.data?.advancedSearchV2?.result || []) as AdvancedSearchBody[]).filter(
    item => item.category !== 'Case',
  );
});

const pagesLength = computed(() => {
  return data?.value?.data?.advancedSearchV2?.max_pages || 0;
});

const title = t('heads.collection.title', { name: collectionInfo.value?.name || '' });
const description = t('heads.collection.description', { name: collectionInfo.value?.name || '' });
// const weaponIcon = getImageFromWaxpeer(collectionInfo.value?.link_name || '', '500:500');

defineOgImageComponent('Dynamic', {
  imageUrl: getImageFromWaxpeer(collectionInfo.value?.link_name || '', '1200:600').replace('f:webp', 'f:png'),
});

const { alternateLinks, canonical } = useAlternateLinks(
  (`/collections/${route.params.id}`) as string,
);

useHead({
  title: () => title,
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items',
    },
  ],
  link: () => alternateLinks.value,
});

useSeoMeta({
  title: () => title,
  ogTitle: () => title,
  description: () => description,
  ogDescription: () => description,
  // ogImage: () => weaponIcon,
  twitterCard: 'summary_large_image',
  twitterTitle: () => title,
  twitterDescription: () => description,
  // twitterImage: () => weaponIcon,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      { name: t('main'), item: '/' },
      {
        name: title,
        item: localePath('/collections'),
      },
      {
        name: collectionInfo.value?.name || '',
        item: localePath((`/collections/${route.params.id}`) as string),
      },
    ],
  }),
  defineItemList({
    name: t('Skins'),
    itemListElement: data.value?.data?.advancedSearchV2.result.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        '@type': 'Product',
        'name': item?.localed_name || item?.market_hash_name || '',
        'description': `${item?.localed_skin ? item.localed_skin : ''} ${item?.localed_rarity ? item.localed_rarity : ''}`,
        'image': getImageFromWaxpeer(`${siteHost}/${item?.link}`),
        'url': localePath(
          getItemLinkWithoutExt({
            name: item?.market_hash_name,
            slug: item?.link,
          }),
        ),
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': item?.rating?.toString() || '0',
          'ratingCount': item?.votes || 0,
        },
      },
    })),
  }),
]);
</script>

<style lang="scss" scoped>

</style>
